import { Suspense, useState } from 'react'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import {
  Chat,
  ChatParticipant,
  Member,
  ModerationEntityType,
  RoleType,
} from '@tribeplatform/gql-client/types'
import {
  useAuthMember,
  useMember,
  useNetwork,
} from '@tribeplatform/react-sdk/hooks'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { T } from '../../i18n/index.js'
import { ReportModal } from '../../ReportModal/ReportModal.js'
import { useChatActions } from '../hooks/useChatActions.js'

import { ActionItem } from './ActionItem.js'

export enum ChatActionsTypes {
  Archive = 'archive',
  Unarchive = 'unarchive',
  Block = 'block',
  Unblock = 'unblock',
}

const DEFAULT_ACTIONS = [
  ChatActionsTypes.Archive,
  ChatActionsTypes.Unarchive,
  ChatActionsTypes.Block,
  ChatActionsTypes.Unblock,
]

interface Props {
  peerParticipant: ChatParticipant
  chat: Chat
  actions?: ChatActionsTypes[]
  title?: React.ReactNode
}

export const ChatActions = ({
  peerParticipant,
  chat,
  actions = DEFAULT_ACTIONS,
  title,
}: Props) => {
  const { archiveChat, blockMember, unblockMember, unarchiveChat } =
    useChatActions({
      chatId: chat._id,
    })
  const { data: authMember } = useAuthMember({
    fields: {
      blockedMembers: 'all',
    },
  })
  const { data: network } = useNetwork()
  const { data: member } = useMember({
    variables: { id: peerParticipant.memberId },
  })
  const isMe = peerParticipant.memberId === authMember?.id
  const [reportModal, setReportModal] = useState(false)
  const isBlocked = authMember?.blockedMembers?.some(
    member => member.id === peerParticipant.memberId,
  )
  const isArchived = chat.currentParticipant?.archivedAt !== null
  const archiveEnabled = actions.includes(ChatActionsTypes.Archive)
  const unarchiveEnabled = actions.includes(ChatActionsTypes.Unarchive)
  const blockEnabled = actions.includes(ChatActionsTypes.Block)
  const unblockEnabled = actions.includes(ChatActionsTypes.Unblock)
  const [canReportMember] = hasScopesPermission(network, [
    'createModerationReport',
  ])
  const isStaff =
    member?.role.type === RoleType.admin ||
    member?.role.type === RoleType.moderator
  const showBlockAction = (blockEnabled || unblockEnabled) && !isMe
  const showArchiveAction = archiveEnabled || unarchiveEnabled
  const showReportAction = canReportMember && !isMe

  if (!showBlockAction && !showArchiveAction && !showReportAction) {
    return null
  }

  return (
    <div className="flex flex-col">
      <div className="text-sm text-content-muted p-3">
        {title ?? (
          <T id="Messaging.ChatActions.Title" defaultMessage="Chat actions" />
        )}
      </div>
      <div className="flex flex-col gap-1 px-1">
        {showArchiveAction && (
          <>
            {!isArchived && (
              <ActionItem
                label={
                  <T
                    id="Messaging.ChatActions.Archive"
                    defaultMessage="Archive Chat"
                  />
                }
                onClick={archiveChat}
                icon={<SvgIcon name="archive" size="lg" />}
              />
            )}
            {isArchived && (
              <ActionItem
                label={
                  <T
                    id="Messaging.ChatActions.Unarchive"
                    defaultMessage="Unarchive Chat"
                  />
                }
                onClick={unarchiveChat}
                icon={<SvgIcon name="archive" size="lg" />}
              />
            )}
          </>
        )}
        {showReportAction && (
          <ActionItem
            label={
              <T id="ChatAction.Member.Report" defaultMessage="Report User" />
            }
            onClick={() => setReportModal(true)}
            icon={<SvgIcon name="status-warning" size="lg" />}
            className="text-interactive-destructive"
          />
        )}
        {showBlockAction && (
          <ActionItem
            disabled={isStaff}
            tooltip={
              isStaff && (
                <T
                  id="Messaging.ChatActions.CannotBlockStaff.Tooltip"
                  defaultMessage="Staff cannot be blocked."
                />
              )
            }
            label={
              isBlocked ? (
                <T
                  id="Messaging.ChatActions.Unblock"
                  defaultMessage="Unblock User"
                />
              ) : (
                <T
                  id="Messaging.ChatActions.Block"
                  defaultMessage="Block User"
                />
              )
            }
            onClick={() => {
              if (isBlocked) {
                unblockMember(peerParticipant.memberId)
              } else {
                blockMember(peerParticipant.memberId)
              }
            }}
            icon={<SvgIcon name="forbidden" size="lg" />}
            className="text-interactive-destructive"
          />
        )}
        {reportModal && (
          <Suspense fallback={<></>}>
            <ReportModal
              entity={
                {
                  id: peerParticipant.memberId,
                } as Member
              }
              entityType={ModerationEntityType.MEMBER}
              onClose={() => setReportModal(false)}
              open
            />
          </Suspense>
        )}
      </div>
    </div>
  )
}
